import {
    setAriaExpanded,
    setAriaHidden,
    setAriaSelected,
    isActivationKey,
    announceToScreenReader,
    setupInteractiveElement,
    removeAriaAttributes,
    setHiddenAndUnfocusable
} from '../Utils/accessibility-utils';

interface IFooterLink {
    href: string;
    ariaLabel: string;
    text: string;
    withDropdown?: boolean;
    subLinks?: IFooterLink[];
}
interface IFooterColumn {
    heading: string;
    links: IFooterLink[];
    button?: IButton;
}

interface ISocialLink {
    href: string;
    ariaLabel: string;
}

interface ILocale {
    code: string;
    label: string;
}
interface ILangSelector {
    locales: ILocale[];
    currentLocale: string;
}

interface IButton {
    text: string;
    ariaLabel: string;
}

export interface IFooterOptions {
    heading: string;
    headingButton: IButton;
    footerColumns: IFooterColumn[];
    linkedin: ISocialLink;
    twitter: ISocialLink;
    facebook: ISocialLink;
    langSelector: ILangSelector;
    backToTopButton: IButton;
    legalLinks: IFooterLink[];
    copyright: string;
}

export class Footer {
    private supportedLocales = ['en', 'fr', 'de', 'es'];

    constructor() {
        this.init();
    }

    private handleDropdownToggle = (evt: Event): void => {
        evt.preventDefault();
        evt.stopPropagation();

        const trigger = evt.currentTarget as HTMLElement;
        const wrapper = trigger.closest('.footer__link-with-dropdown') as HTMLElement;
        if (!wrapper) return;

        const sublinks = wrapper.querySelector('.footer__sublinks') as HTMLElement;
        const isOpen = wrapper.classList.toggle('active');
        setHiddenAndUnfocusable(sublinks, !isOpen);
        setAriaExpanded(trigger, isOpen);

        if (isOpen && evt instanceof KeyboardEvent) {
            const first = sublinks.querySelector<HTMLElement>('a, button');
            if (first) setTimeout(() => first.focus(), 20);
        }

        announceToScreenReader(
            `${trigger.textContent?.trim()} submenu ${isOpen ? 'expanded' : 'collapsed'}`
        );
    };

    private handleColumnToggle = (evt: Event): void => {
        if (window.innerWidth > 768) return;

        evt.preventDefault();
        evt.stopPropagation();

        const header = evt.currentTarget as HTMLElement;
        const column = header.closest('.footer__column') as HTMLElement;
        if (!column) return;

        const list = column.querySelector('.footer__links-list') as HTMLElement;
        const isOpen = column.classList.toggle('active');
        setAriaExpanded(header, isOpen);

        setHiddenAndUnfocusable(list, !isOpen);

        if (isOpen && evt instanceof KeyboardEvent) {
            const first = list.querySelector<HTMLElement>('a, button');
            if (first) setTimeout(() => first.focus(), 20);
        }

        const title = header.querySelector('.footer__column-title')?.textContent?.trim();
        if (title) {
            announceToScreenReader(`${title} section ${isOpen ? 'expanded' : 'collapsed'}`);
        }
    };

    private updateHeaderAccessibility = (): void => {
        document.querySelectorAll<HTMLElement>('.footer__column-header').forEach(header => {
            if (window.innerWidth <= 768) {
                setupInteractiveElement(header, {
                    role: 'button',
                    tabindex: 0,
                    ariaExpanded: header.hasAttribute('aria-expanded') ? undefined : false
                });
            } else {
                removeAriaAttributes(header, ['role', 'tabindex', 'aria-expanded']);
            }
        });
    };

    private setupLanguageSelector(
        languageWrapper: HTMLElement | null,
        languageSelector: HTMLElement | null,
        languageOptions: HTMLElement | null
    ): void {
        const closeOptions = (): void => {
            if (!languageOptions || !languageWrapper || !languageSelector) return;
            languageOptions.classList.remove('active', 'footer__language-options--up');
            languageWrapper.classList.remove('active');
            setAriaExpanded(languageSelector, false);
            setHiddenAndUnfocusable(languageOptions, true);
        };

        const toggleOptions = (evt: Event): void => {
            evt.stopPropagation();
            if (!languageOptions || !languageWrapper || !languageSelector) return;

            const isOpening = !languageOptions.classList.contains('active');

            languageOptions.classList.toggle('active', isOpening);
            languageWrapper.classList.toggle('active', isOpening);
            setAriaExpanded(languageSelector, isOpening);
            setHiddenAndUnfocusable(languageOptions, !isOpening);

            if (isOpening) {
                const footerRect = document.querySelector('.footer')!.getBoundingClientRect();
                const selectorRect = languageSelector.getBoundingClientRect();
                const spaceBelow = footerRect.bottom - selectorRect.bottom;
                const needed = languageOptions.getBoundingClientRect().height + 10;
                languageOptions.classList.toggle(
                    'footer__language-options--up',
                    spaceBelow < needed
                );
            }
        };

        if (languageSelector) {
            languageSelector.addEventListener('click', toggleOptions);
            languageSelector.addEventListener('keydown', e => {
                if (isActivationKey(e)) {
                    e.preventDefault();
                    toggleOptions(e);
                } else if (
                    (e as KeyboardEvent).key === 'Escape' &&
                    languageOptions?.classList.contains('active')
                ) {
                    e.preventDefault();
                    closeOptions();
                    languageSelector.focus();
                }
            });
            languageSelector.addEventListener('blur', () =>
                setTimeout(() => {
                    const active = document.activeElement;
                    if (
                        !active?.closest('.footer__language-options') &&
                        !active?.closest('.footer__language-wrapper')
                    ) {
                        closeOptions();
                    }
                })
            );
        }

        document.addEventListener('click', closeOptions);

        if (languageOptions) {
            languageOptions.addEventListener('click', e => {
                const option = (e.target as HTMLElement).closest(
                    '.footer__language-option'
                ) as HTMLElement;
                const code = option?.dataset.language;
                if (code) this.selectLanguage(code);
            });

            languageOptions.addEventListener('keydown', e => {
                const key = (e as KeyboardEvent).key;
                if (key !== 'ArrowDown' && key !== 'ArrowUp') return;
                e.preventDefault();

                const opts = Array.from(
                    languageOptions.querySelectorAll<HTMLElement>('.footer__language-option')
                );
                const idx = opts.indexOf(document.activeElement as HTMLElement);
                const next =
                    key === 'ArrowDown'
                        ? (idx + 1) % opts.length
                        : (idx - 1 + opts.length) % opts.length;
                opts[next].focus();
            });
        }
    }

    private selectLanguage(code: string): void {
        const langDisplay = document.querySelector('.footer__language-current') as HTMLElement | null;
        if (langDisplay) langDisplay.textContent = code;

        document.querySelectorAll('.footer__language-option').forEach(opt => {
            setAriaSelected(
                opt as HTMLElement,
                opt.getAttribute('data-language') === code
            );
        });

        const parts = window.location.pathname.split('/').filter(Boolean);
        const lower = code.toLowerCase();
        if (parts[0] && this.supportedLocales.includes(parts[0].toLowerCase())) parts[0] = lower;
        else parts.unshift(lower);
        window.location.href = '/' + parts.join('/');
    }

    private init(): void {
        const backToTop = document.querySelectorAll('.footer__back-to-top button, .footer__back-to-top span');
        const languageWrapper = document.querySelector('.footer__language-wrapper');
        const languageSelector = document.querySelector('.footer__language-selector');
        const languageOptions = document.querySelector('.footer__language-options');

        backToTop?.forEach(function (selector) {
            selector?.addEventListener('click', e => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
            });
        });

        this.setupLanguageSelector(
            languageWrapper as HTMLElement,
            languageSelector as HTMLElement,
            languageOptions as HTMLElement
        );

        document.querySelectorAll('.footer__link-with-dropdown .footer__link').forEach(link => {
            const clone = link.cloneNode(true) as HTMLElement;
            link.parentNode!.replaceChild(clone, link);

            clone.addEventListener('click', this.handleDropdownToggle);
            clone.addEventListener('keydown', e => {
                if (isActivationKey(e)) this.handleDropdownToggle(e);
            });

            setupInteractiveElement(clone, {
                role: 'button',
                tabindex: 0,
                ariaExpanded: false
            });
            const sublinks = clone.nextElementSibling as HTMLElement;
            if (sublinks) setHiddenAndUnfocusable(sublinks, true);
        });

        document.querySelectorAll('.footer__column-header').forEach(header => {
            const clone = header.cloneNode(true) as HTMLElement;
            header.parentNode!.replaceChild(clone, header);

            clone.addEventListener('click', this.handleColumnToggle);
            clone.addEventListener('keydown', e => {
                if (isActivationKey(e)) this.handleColumnToggle(e);
            });

            setupInteractiveElement(clone, {
                role: 'button',
                tabindex: 0,
                ariaExpanded: false
            });

            const list = clone.parentElement?.querySelector('.footer__links-list') as HTMLElement;
            if (list && window.innerWidth <= 768) {
                setHiddenAndUnfocusable(list, true);
            } else if (list) {
                setAriaHidden(list, false);
            }
        });

        document.querySelectorAll<HTMLElement>('.footer__sublinks').forEach(el => setHiddenAndUnfocusable(el, true));

        this.updateHeaderAccessibility();
        window.addEventListener('resize', this.updateHeaderAccessibility);
    }
}

document.addEventListener('DOMContentLoaded', () => {
    if (document.querySelector('.footer')) new Footer();
});